import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../apiClient";
import { useTranslation } from "react-i18next";

export default function ProductsPage() {
  // ✅ ALL HOOKS FIRST
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ✅ NEW: Form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", size: "", price_groszy: "" });
  const [submitting, setSubmitting] = useState(false);

  const userRole = localStorage.getItem("userRole");

  useEffect(() => {
    // ✅ FIXED: Allow "partner" to view
    if (!["admin", "manager", "partner"].includes(userRole)) {
      // ✅ Redirect instead of showing error
      navigate("/production");
      setLoading(false);
      return;
    }

    const fetchProducts = async () => {
      try {
        const data = await apiClient.get("/products/");
        setProducts(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [userRole, navigate]);

  // ✅ NEW: Handle form submit
  const handleAddProduct = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const newProduct = await apiClient.post("/products/", {
        name: formData.name,
        size: formData.size,
        price_groszy: parseInt(formData.price_groszy),
      });

      setProducts([...products, newProduct]);
      setFormData({ name: "", size: "", price_groszy: "" });
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Access control UI — REMOVED ERROR MESSAGE
  if (!["admin", "manager", "partner"].includes(userRole)) {
    return null; // ✅ Don't render anything, redirect already happened
  }

  // ✅ Main UI
  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 bg-[#F5E9D8]">
      <h2 className="text-2xl font-bold text-[#23265E] text-center">
        {t("products")}
      </h2>

      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 p-3 rounded text-sm">
          ❌ {error}
        </div>
      )}

      {/* ✅ NEW: Add Product Button (admin only) */}
      {userRole === "admin" && (
        <div className="text-center">
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-[#23265E] text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
          >
            {showForm ? "❌ Cancel" : "➕ Add Product"}
          </button>
        </div>
      )}

      {/* ✅ NEW: Add Product Form */}
      {showForm && userRole === "admin" && (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-[#23265E] border-opacity-20">
          <h3 className="font-semibold text-[#23265E] mb-3">Add New Product</h3>
          <form onSubmit={handleAddProduct} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[#23265E] mb-1">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-[#23265E] border-opacity-30 rounded px-3 py-2 text-[#23265E]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#23265E] mb-1">
                Size *
              </label>
              <input
                type="text"
                required
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                className="w-full border border-[#23265E] border-opacity-30 rounded px-3 py-2 text-[#23265E]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#23265E] mb-1">
                Price (groszy) *
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.price_groszy}
                onChange={(e) => setFormData({ ...formData, price_groszy: e.target.value })}
                className="w-full border border-[#23265E] border-opacity-30 rounded px-3 py-2 text-[#23265E]"
                placeholder="e.g., 1500 for 15.00 PLN"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#23265E] text-white py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
            >
              {submitting ? "Adding..." : "✅ Add Product"}
            </button>
          </form>
        </div>
      )}

      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#23265E] border-opacity-20">
        <h3 className="font-semibold text-[#23265E] mb-3">
          {t("productList")} ({products.length})
        </h3>

        {loading ? (
          <p className="text-center text-[#23265E] text-sm italic">
            {t("loading")}...
          </p>
        ) : products.length === 0 ? (
          <p className="text-center text-[#23265E] text-sm italic">
            {t("noProducts")}
          </p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {products.map((p) => (
              <div
                key={p.id}
                className="p-3 border border-[#23265E] border-opacity-20 rounded-lg bg-[#F5E9D8]"
              >
                <div className="font-semibold text-[#23265E]">{p.name}</div>

                {p.size && (
                  <div className="text-sm text-[#23265E] mt-1">
                    📏 <span className="font-medium">Size:</span> {p.size}
                  </div>
                )}

                {p.price_groszy !== undefined && (
                  <div className="text-sm text-[#23265E] mt-1">
                    💰 <span className="font-medium">Price:</span>{" "}
                    {(p.price_groszy / 100).toFixed(2)} PLN
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}